import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from "@angular/core";
import { AnimalRepository } from "../states/animal.repository";
import { ActivatedRoute, Router } from "@angular/router";
import { catchError, distinctUntilChanged, filter, forkJoin, map, of, switchMap, take, takeWhile } from "rxjs";
import { AnimalEditService } from "../services/animal-edit.service";
import { AnimalModel } from "../models/animal.model";
import { GalleriaResponsiveOptions } from 'primeng/galleria';
import { getCharacteristicsEnumKey } from "../models/animal-characteristics.enum";
import { AnimalDetailsTimelineComponent } from '../animal-details-timeline/animal-details-timeline.component';
import { ToggleSwitchChangeEvent } from "primeng/toggleswitch";
import { ConfirmationService, MenuItem } from "primeng/api";
import { AnimalSheltersRepository } from '../../../shared/states/animal-shelter.repository';
import { CanComponentDeactivate } from 'src/app/shared/guard/can-deactivate.guard';
import { AnimalDetailsWhereaboutsComponent } from '../animal-details-whereabouts/animal-details-whereabouts.component';
import { TranslocoService } from '@jsverse/transloco';
import { AnimalChangeShelterService } from "../services/animal-change-shelter.service";
import { AnimalShelterChange } from "../models/animal-shelter-change.model";
import { AnimalAddService } from "../services/animal-add.service";
import { AnimalOriginModel } from '../models/animal-origin.model';
import { ContactRepository } from '../../contact/states/contact.repository';
import { AnimalOriginRepository, originStore } from '../states/animal-origin.repository';
import { AnimalWhereaboutsRepository, animalWhereaboutsStore } from '../states/animal-whereabouts.repository';
import { Location } from '@angular/common';
import { select } from '@ngneat/elf';
import { AnimalOriginReasonForTransfer } from '../models/animal-origin-reason-for-transfer.enum';
import { AnimalContactRef } from '../models/animal-contact-ref.interface';
import { ContactModel } from '../../contact/models/contact.model';
import { OriginContactEntry } from '../services/animal-add.service';
import { FoundProcessSharedInput } from 'src/app/shared/components/duplicate-animals/duplicate-animals.component';
import { CompetentAuthoritiesRepository } from 'src/app/shared/states/competent-authorities.repository';

@Component({
  selector: 'app-animal-details',
  templateUrl: './animal-details.component.html',
  styleUrls: ['./animal-details.component.scss'],
  standalone: false
})
export class AnimalDetailsComponent implements OnInit, AfterViewInit, OnDestroy, CanComponentDeactivate {
  @ViewChild('animalDetailsTimelineComponent') animalDetailsTimelineComponent!: AnimalDetailsTimelineComponent;
  @ViewChildren('masterWhereabouts') private masterWhereaboutsRefs!: QueryList<AnimalDetailsWhereaboutsComponent>;
  @ViewChild('animalTabsEl', { read: ElementRef }) private animalTabsElRef!: ElementRef;

  private _tabCaptureHandler: ((e: MouseEvent) => void) | null = null;
  get whereaboutsComponent(): AnimalDetailsWhereaboutsComponent | undefined {
    return this.masterWhereaboutsRefs?.first;
  }

  get hasMasterInFirstRow(): boolean {
    return this.pairRows.length > 0 && this.pairRows[0]?.whereaboutId == null;
  }
  alive: boolean;
  animal: AnimalModel | null = null;
  responsiveOptions: GalleriaResponsiveOptions[] = [];
  editTabsDialogVisible: { [tab: string]: boolean | string } = {};
  showEditDialog: boolean = false;
  showMoveDialog: boolean = false;
  showMoveHistoryDialog: boolean = false;
  filteredCoatTypes: string[] | undefined = [];
  animalEditMenuItems: MenuItem[] | undefined;
  motherAnimal: AnimalModel | null = null;
  previousBookNumbersWithShelter: { bookNumber: string; shelterName: string | null }[] = [];
  selectedTabIndex: number = 0;
  objectKeys = Object.keys;
  fromOtherShelter: boolean = false;
  animalShelterChangeHistories: AnimalShelterChange[] = [];
  undoShelterChangeLoading: boolean = false;

  addDialogVisible: boolean = false;
  duplicateAnimalDialogVisible: boolean = false;
  duplicateOrigin?: AnimalOriginModel;
  duplicateFoundMode = false;
  duplicateFoundShared: FoundProcessSharedInput | null = null;

  originPairIds: number[] = [];
  whereaboutPairIds: number[] = [];
  pairRows: { originId: number | null; whereaboutId: number | null }[] = [];

  constructor(
    private readonly _animalRepository: AnimalRepository,
    private readonly _route: ActivatedRoute,
    private readonly _animalEditService: AnimalEditService,
    private readonly _animalSheltersRepository: AnimalSheltersRepository,
    private readonly _confirmationService: ConfirmationService,
    private readonly _translocoService: TranslocoService,
    private readonly _animalChangeShelterService: AnimalChangeShelterService,
    private readonly _animalAddService: AnimalAddService,
    private readonly _contactRepository: ContactRepository,
    private readonly _animalOriginRepository: AnimalOriginRepository,
    private readonly _animalWhereaboutsRepository: AnimalWhereaboutsRepository,
    private readonly _location: Location,
    private readonly _router: Router,
    private readonly _competentAuthoritiesRepository: CompetentAuthoritiesRepository
  ) {
    this.alive = true;
  }

  ngOnInit(): void {
    if (!this.animal?.oldBookNumberList?.length && this.animal?.oldBookNumber) {
      this.animal.oldBookNumberList = [this.animal.oldBookNumber];
    }
    this._animalAddService.addDialogVisible.subscribe((openModal) => {
      this.addDialogVisible = openModal;
    });

    this._animalEditService.editDialogVisible.pipe(takeWhile(() => this.alive)).subscribe((openModal) => {
      this.editTabsDialogVisible = openModal;
      this.showEditDialog = openModal && Object.keys(openModal).length ? true : false;
    });

    this._animalEditService.moveDialogVisible.pipe(takeWhile(() => this.alive)).subscribe((openModal) => {
      this.showMoveDialog = openModal;
    });

    this._animalEditService.moveHistoryDialogVisible.pipe(takeWhile(() => this.alive)).subscribe((openModal) => {
      this.showMoveHistoryDialog = openModal;
    });

    this.responsiveOptions = [
      {
        breakpoint: '1024px',
        numVisible: 5
      },
      {
        breakpoint: '768px',
        numVisible: 3
      },
      {
        breakpoint: '560px',
        numVisible: 1
      }
    ];

    // Animal laden
    this._route.paramMap
      .pipe(
        takeWhile(() => this.alive),
        map(params => params.get('id')),
        filter((idStr): idStr is string => idStr !== null),
        map(idStr => parseInt(idStr, 10)),
        distinctUntilChanged(),
        switchMap(id => this._animalRepository.getById(id))
      )
      .subscribe((animal) => {
        if (!animal) {
          this.animal = null as any;
          return;
        }
        this.handleAnimalLoaded(animal);
      });

    this._route.queryParamMap
      .pipe(
        takeWhile(() => this.alive),
      )
      .subscribe(qp => {
        this.selectedTabIndex = Number(qp.get('tabNumber')) || 0;
      });

  }

  goBack() {
    if (window.history.length > 1) {
      this._location.back();
    } else {
      this._router.navigate(['/dashboard']);
    }
  }

  private handleAnimalLoaded(animal: AnimalModel | null): void {
    if (!animal) {
      this.animal = null as any;
      this.motherAnimal = null as any;
      this.previousBookNumbersWithShelter = [];
      this.updateAnimalEditMenuItems();
      return;
    }

    this.animal = animal;

    if (!this.animal.oldBookNumberList?.length && this.animal.oldBookNumber) {
      this.animal.oldBookNumberList = [this.animal.oldBookNumber];
    }

    this.fromOtherShelter =
      animal.animalShelter.id !== this._animalSheltersRepository.getActiveAnimalShelter()?.id;

    this.filteredCoatTypes = animal.coatType?.filter(x => x !== null && x !== '') ?? [];

    if (animal.motherId) {
      this._animalRepository.getById(animal.motherId)
        .pipe(take(1))
        .subscribe((motherAnimal) => {
          this.motherAnimal = motherAnimal!;
          this.updateAnimalEditMenuItems();
        });
    } else {
      this.motherAnimal = null as any;
    }

    if (animal.previousBookNumbers && animal.previousBookNumbers.length > 0) {
      this.previousBookNumbersWithShelter = [];
      this._animalSheltersRepository.getAnimalShelters()
        .pipe(take(1))
        .subscribe((animalShelters) => {
          animal.previousBookNumbers.forEach((entry: any) => {
            const shelter = animalShelters.find((shelter) => shelter.id === entry.animalShelterId);
            this.previousBookNumbersWithShelter.push({
              bookNumber: entry.bookNumber,
              shelterName: shelter ? shelter.title : null,
            });
          });
          this.updateAnimalEditMenuItems();
        });
    } else {
      this.previousBookNumbersWithShelter = [];
    }

    this.loadShelterChangeHistories();
    this.updateAnimalEditMenuItems();
    this.loadPairData(animal.id);
  }

  private loadPairData(animalId: number): void {
    this._animalOriginRepository.getByAnimalId(animalId).pipe(take(1)).subscribe();
    this._animalWhereaboutsRepository.getByAnimalId(animalId, false).pipe(take(1)).subscribe();

    originStore.pipe(
      select(state => state.origins[animalId] ?? []),
      takeWhile(() => this.alive)
    ).subscribe(origins => {
      this.originPairIds = [...origins].sort((a, b) => b.id - a.id).map(o => o.id);
      this.updatePairRows();
    });

    animalWhereaboutsStore.pipe(
      select(state => state.whereabouts?.[animalId] ?? []),
      takeWhile(() => this.alive)
    ).subscribe(whereabouts => {
      this.whereaboutPairIds = [...(whereabouts ?? [])].sort((a, b) => b.id - a.id).map(w => w.id);
      this.updatePairRows();
    });
  }

  private updatePairRows(): void {
    const oCount = this.originPairIds.length;
    const wCount = this.whereaboutPairIds.length;
    const count = Math.max(oCount, wCount);
    const oOffset = Math.max(0, wCount - oCount);
    const wOffset = Math.max(0, oCount - wCount);

    this.pairRows = Array.from({ length: count }, (_, i) => {
      const oIdx = i - oOffset;
      const wIdx = i - wOffset;
      return {
        originId: oIdx >= 0 ? (this.originPairIds[oIdx] ?? null) : null,
        whereaboutId: wIdx >= 0 ? (this.whereaboutPairIds[wIdx] ?? null) : null
      };
    });
  }

  get oldBookNumberListReversed(): string[] {
    if (!this.animal?.oldBookNumberList?.length) {
      return [];
    }

    // Kopie + reverse, damit das Original nicht mutiert wird
    return [...this.animal.oldBookNumberList].reverse();
  }

  updateAnimalEditMenuItems() {
    this.animalEditMenuItems = [
      {
        items: [
          {
            label: 'Tier duplizieren',
            icon: 'pi pi-clone',
            command: () => this.openAddDialogDuplicateAnimal()
          },
          {
            label: 'Wiederaufnahme',
            icon: 'pi pi-undo',
            command: () => this.openAddDialogWithResume(),
            visible: !!this.animal && !this.animal.inStock
          },
          {
            label: 'In Tierheim verschieben',
            icon: 'pi pi-upload',
            command: () => this.toggleMoveDialog()
          },
          {
            label: 'Verschiebungs-Historie',
            icon: 'pi pi-history',
            command: () => this.toggleMoveHistoryDialog(),
            visible: this.animalShelterChangeHistories.length > 0,
            disabled: this.undoShelterChangeLoading
          }
        ]
      }
    ];
  }

  openAddDialogWithResume() {
    if (!this.animal) return;
    this._animalAddService.setResumeAnimal(this.animal);
    this._animalAddService.toggleAddDialog(true);

    this.addDialogVisible = true;
  }

  private pickLatestOrigin(origins: AnimalOriginModel[] | null | undefined): AnimalOriginModel | undefined {
    if (!origins || origins.length === 0) return undefined;
    return [...origins].sort((a, b) => (b?.id ?? 0) - (a?.id ?? 0))[0];
  }

  openAddDialogDuplicateAnimal() {
    if (!this.animal) return;

    this._animalOriginRepository.getByAnimalId(this.animal.id, /*cached*/ true)
      .pipe(take(1))
      .subscribe({
        next: (origins) => {
          const latest = this.pickLatestOrigin(origins);
          this.duplicateOrigin = latest;
          this.duplicateFoundMode = latest?.type === AnimalOriginReasonForTransfer.FOUND_ANIMAL;
          if (this.duplicateFoundMode && latest) {
            this.prepareFoundDuplicate(latest);
          } else {
            this.duplicateFoundShared = null;
            this.duplicateAnimalDialogVisible = true;
          }
        },
        error: () => {
          this.duplicateOrigin = undefined;
          this.duplicateFoundMode = false;
          this.duplicateFoundShared = null;
          this.duplicateAnimalDialogVisible = true;
        }
      });
  }

  /** Lädt die Behörden-Kontakte (für den PDF-Versand) und öffnet dann den Fundtier-Duplikat-Dialog. */
  private prepareFoundDuplicate(origin: AnimalOriginModel): void {
    const shelterId = this.animal?.animalShelter?.id ?? origin.animalShelter?.id;
    const authorityId = origin.competentAuthority?.id;

    const authority$ = (shelterId && authorityId)
      ? this._competentAuthoritiesRepository.getCompetentAuthoritiesByShelterId(shelterId).pipe(
          take(1),
          map(auths => auths.find(a => a.id === authorityId)?.contacts ?? []),
          catchError(() => of([] as ContactModel[]))
        )
      : of([] as ContactModel[]);

    // Volle ContactModels der gespeicherten Kontakt-Refs laden (für PDF-Vorschau + Versand)
    const contactIds = Array.from(new Set(
      (origin.contacts ?? []).map(r => r.contact).filter((x): x is number => typeof x === 'number')
    ));
    const contacts$ = contactIds.length
      ? forkJoin(contactIds.map(id => this._contactRepository.getById(id).pipe(take(1), catchError(() => of(null)))))
      : of([] as (ContactModel | null)[]);

    forkJoin([authority$, contacts$]).pipe(take(1)).subscribe(([authorityContacts, loaded]) => {
      const contactsById: Record<number, ContactModel> = {};
      (loaded ?? []).forEach(c => { if (c?.id != null) contactsById[c.id] = c; });
      this.duplicateFoundShared = this.buildFoundSharedFromOrigin(origin, authorityContacts, contactsById);
      this.duplicateAnimalDialogVisible = true;
    });
  }

  /**
   * Baut die geteilten Fundtier-Daten aus dem bestehenden (gespeicherten) Fundtier-Origin.
   * Das Original selbst wird beim Duplizieren NICHT mitgeführt (foundIncludeOriginal=false),
   * aber für den PDF-Versand über originalOriginId einbezogen.
   */
  private buildFoundSharedFromOrigin(
    origin: AnimalOriginModel,
    authorityContacts: ContactModel[],
    contactsById: Record<number, ContactModel>
  ): FoundProcessSharedInput {
    return {
      shelterId: this.animal?.animalShelter?.id ?? origin.animalShelter?.id ?? 0,
      foundStatus: origin.foundStatus ?? null,
      foundDateTime: origin.foundDateTime ?? origin.dateOfReceipt ?? null,
      pickupUserName: origin.pickupUserName ?? null,
      pickUpDateTime: origin.pickupDateTime ?? null,
      consentToShareWithOwner: !!origin.consentToShareWithOwner,
      competentAuthorityId: origin.competentAuthority?.id ?? null,
      signatureLocation: null,
      signatureDate: null,
      contacts: this.toOriginContactEntries(origin.contacts, contactsById),
      authorityContacts,
      originalOriginId: origin.id ?? null,
    };
  }

  /** Wandelt gespeicherte Kontakt-Refs in OriginContactEntry (existierend) um – mit vollem ContactModel (Fallback: Id). */
  private toOriginContactEntries(refs: AnimalContactRef[], contactsById: Record<number, ContactModel>): OriginContactEntry[] {
    return (refs ?? [])
      .filter(r => !!r.contact)
      .map(r => ({
        meta: { id: r.id, type: r.type, label: r.label ?? null, isPrimary: !!r.isPrimary },
        data: {
          contact: contactsById[r.contact!] ?? ({ id: r.contact } as ContactModel),
          isValid: true,
          allowEmptyValid: false,
        },
      }));
  }

  ngAfterViewInit(): void {
    this._tabCaptureHandler = (e: MouseEvent) => {
      const wc = this.whereaboutsComponent;
      const shouldConfirm =
        wc?.hasUnsavedData &&
        !wc.isFoundAnimalReturnSelected &&
        !!wc.selectedTypeOption?.['dummy'];
      if (!shouldConfirm) return;

      const tabEl = (e.target as HTMLElement).closest('[role="tab"]');
      if (!tabEl) return;

      const tabs = Array.from(tabEl.closest('[role="tablist"]')?.querySelectorAll('[role="tab"]') ?? []);
      const tabIndex = tabs.indexOf(tabEl as Element);

      if (tabIndex < 0 || tabIndex === this.selectedTabIndex) return;

      e.stopPropagation();

      this.showConfirmDialog(undefined);
    };
    this.animalTabsElRef.nativeElement.addEventListener('click', this._tabCaptureHandler, true);
  }

  onTabOpenChange(event: number | string | undefined) {
    const tabNumber = typeof event === 'number' ? event : parseInt(event as string, 10);
    this.navigateToTab(tabNumber);
  }

  private navigateToTab(tabNumber: number): void {
    this.selectedTabIndex = tabNumber;
    if (tabNumber === 4) {
      this.animalDetailsTimelineComponent.loadData();
    }
    this._router.navigate([], {
      relativeTo: this._route,
      queryParams: { tabNumber },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  ngOnDestroy(): void {
    if (this._tabCaptureHandler) {
      this.animalTabsElRef?.nativeElement?.removeEventListener('click', this._tabCaptureHandler, true);
    }
    this.alive = false;
    this.showEditDialog = false;
  }

  protected readonly getCharacteristicsEnumKey = getCharacteristicsEnumKey;

  toggleEditDialog() {
    this._animalEditService.toggleEditDialog({ all: true });
  }

  private toggleMoveDialog() {
    this._animalEditService.toggleMoveDialog();
  }

  private toggleMoveHistoryDialog() {
    this._animalEditService.toggleMoveHistoryDialog()
  }

  onSyncWithWebsiteChange(id: number, $event: ToggleSwitchChangeEvent) {
    this._animalRepository.setSyncWithWebsite(id, $event.checked);
  }

  onSanctuaryChange(id: number, $event: ToggleSwitchChangeEvent) {
    this._animalRepository.setSanctuary(id, $event.checked);
  }

  onFosterHomeWantedChange(id: number, $event: ToggleSwitchChangeEvent) {
    this._animalRepository.setFosterHomeWanted(id, $event.checked);
  }

  onSponsoredChange(id: number, $event: ToggleSwitchChangeEvent) {
    this._animalRepository.setSponsored(id, $event.checked);
  }

  onOutdoorChange(id: number, $event: ToggleSwitchChangeEvent) {
    this._animalRepository.setOutdoor(id, $event.checked);
  }

  canDeactivate(): Promise<boolean> {
    const wc = this.whereaboutsComponent;
    const shouldConfirm =
      wc?.hasUnsavedData &&
      !wc.isFoundAnimalReturnSelected &&
      !!wc.selectedTypeOption?.['dummy'];

    if (shouldConfirm) {
      return new Promise<boolean>((resolve) => {
        this.showConfirmDialog(resolve);
      });
    }

    return Promise.resolve(true);
  }

  showConfirmDialog(resolve?: (value: boolean) => void) {
    this._confirmationService.confirm({
      message: this._translocoService.translate('confirmDialog.message.unsavedData'),
      header: this._translocoService.translate('confirmDialog.header.confirmation'),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: "p-button-danger p-button-text",
      rejectButtonStyleClass: "p-button-text p-button-text",
      acceptLabel: this._translocoService.translate('confirmDialog.acceptLabel.save'),
      rejectLabel: this._translocoService.translate('confirmDialog.acceptLabel.reject'),
      accept: () => {
        this.whereaboutsComponent?.saveNewWhereabout();
        if (resolve) resolve(true);
      },
      reject: () => {
        this.whereaboutsComponent?.cancelNewWhereabout();
        if (resolve) resolve(true);
      }
    });
  }

  loadShelterChangeHistories() {
    if (!this.animal) return;
    this._animalChangeShelterService.getShelterChangeHistories(this.animal.id).subscribe({
      next: (histories) => {
        this.animalShelterChangeHistories = histories || [];
        this.updateAnimalEditMenuItems();
      },
      error: () => {
        this.animalShelterChangeHistories = [];
        this.updateAnimalEditMenuItems();
      }
    });
  }
}
